import { Injectable } from '@angular/core';
import { capSQLiteChanges, capSQLiteValues } from '@capacitor-community/sqlite';
import { ISite, LatLon, Listing, Site } from '@curbnturf/entities';
import { firstValueFrom, of } from 'rxjs';
import { DatabaseService } from '../database.service';
import { IDbSet } from '../dbset.interface';
import { SiteEntity } from '../entities/site';

const TABLE_NAME = 'sites';

@Injectable({
  providedIn: 'root',
})
export class SiteDbSet implements IDbSet {
  public static readonly siteFields = [
    'id',
    'type',
    'name',
    'lat',
    'lng',
    'address',
    'city',
    'state',
    'zipcode',
    'user_id',
  ];

  constructor(private databaseService: DatabaseService) {}

  public store(request: ISite): Promise<capSQLiteValues> {
    const entity = this.mapObjectsToStorable([request]).pop();
    if (entity) {
      return this.databaseService.insert<SiteEntity>(TABLE_NAME, entity);
    } else {
      throw new Error('Unable to create storable object.');
    }
  }

  public storeArray(requests: ISite[], ignoreDuplicates: boolean = false): Promise<capSQLiteValues> {
    const mappedObjects = this.mapObjectsToStorable(requests);
    return this.databaseService.insertMulti<SiteEntity>(
      TABLE_NAME,
      mappedObjects,
      SiteDbSet.siteFields,
      ignoreDuplicates,
    );
  }

  public async truncateAndStoreArray(records: ISite[], ignoreDuplicates: boolean = false): Promise<capSQLiteValues> {
    await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
    return this.storeArray(records, ignoreDuplicates);
  }

  public async truncate(): Promise<capSQLiteChanges> {
    return await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
  }

  public async retrieve(id: number): Promise<ISite> {
    const results = await this.databaseService.select(TABLE_NAME, { id });
    const objResults = this.mapResultsObject(results);
    if (objResults && objResults?.length > 0) {
      return objResults[0];
    } else {
      return new Site();
    }
  }

  public async retrieveOwnSites(userId?: number): Promise<ISite[]> {
    if (userId) {
      const results = await this.databaseService.select(TABLE_NAME, { user_id: userId });
      return this.mapResultsObject(results);
    } else {
      return firstValueFrom(of([]));
    }
  }

  public async retrieveAll(limit?: number): Promise<ISite[]> {
    const results = await this.databaseService.selectAll(TABLE_NAME, undefined, limit);
    return this.mapResultsObject(results);
  }

  public async remove(id: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { id });
  }

  private mapResultsObject(results: capSQLiteValues): ISite[] {
    if (results.values) {
      return results.values?.map(
        (record) =>
          new Site({
            id: record.id || undefined,
            listing: record.listing_id ? new Listing({ id: record.listing_id }) : undefined,
            name: record.name,
            coordinates: new LatLon({
              lat: record.lat || 0,
              lon: record.lng || 0,
            }),
            photos: [],
          }),
      );
    } else {
      return [];
    }
  }

  private mapObjectsToStorable(items: ISite[]): SiteEntity[] {
    if (items) {
      return items?.map((record: ISite) => ({
        // Can't use spread operator here as it tries to enter null in undefined properties as columns
        id: record.id,
        listingId: record.listing?.id,
        name: record.name,
        lat: record.coordinates?.lat,
        lng: record.coordinates?.lon,
      }));
    } else {
      return [];
    }
  }
}
